// services/lookoutShareService.js
import { API_URL } from '../config/api';
import { getAuthHeaders, getCurrentUser } from '../utils/auth';

// Reuses the existing reminders "assignment dropdown" endpoint rather than
// standing up a new user-listing endpoint - same active-employee list, just
// filtered down to exclude the current user (can't share with yourself).
export async function fetchShareableUsers() {
  const response = await fetch(`${API_URL}/api/reminders/users`);
  if (!response.ok) throw new Error('Failed to fetch users');
  const data = await response.json();
  const currentUser = getCurrentUser();
  return (data.users || []).filter(u => u.UserName !== currentUser);
}

// items: [{ orderNo, customerName, dueDate }]. sharedWith: [username, ...]
export async function shareLookoutItems(items, sharedWith) {
  const response = await fetch(`${API_URL}/api/lookout-shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ items, sharedWith })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to share items');
  }
  return response.json();
}

export async function fetchLookoutInbox(username) {
  const response = await fetch(`${API_URL}/api/lookout-shares/inbox?username=${encodeURIComponent(username)}`);
  if (!response.ok) throw new Error('Failed to fetch shared items');
  const data = await response.json();
  return data.shares || [];
}

// Shares sent out by this user (SharedBy = them) - who they shared each
// order with, and whether that recipient has added it yet.
export async function fetchSentLookoutShares(username) {
  const response = await fetch(`${API_URL}/api/lookout-shares/sent?username=${encodeURIComponent(username)}`);
  if (!response.ok) throw new Error('Failed to fetch sent shares');
  const data = await response.json();
  return data.shares || [];
}

export async function fetchAddedLookoutShares(username) {
  const response = await fetch(`${API_URL}/api/lookout-shares/added?username=${encodeURIComponent(username)}`);
  if (!response.ok) throw new Error('Failed to fetch shared items');
  const data = await response.json();
  return data.shares || [];
}

// Pulls a shared item into the recipient's "My List" view.
export async function addLookoutShare(id) {
  const response = await fetch(`${API_URL}/api/lookout-shares/${id}/add`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
  });
  if (!response.ok) throw new Error('Failed to add shared item');
  return response.json();
}

// Recipient declines a share from their inbox, or removes an already-added
// one from "My List". Flags it instead of deleting, so the sender still sees
// it (and that it was dismissed) in their "sent" history.
export async function dismissLookoutShare(id) {
  const response = await fetch(`${API_URL}/api/lookout-shares/${id}/dismiss`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
  });
  if (!response.ok) throw new Error('Failed to dismiss shared item');
  return response.json();
}

// Sender revokes a share they sent - actually deletes the row. Only valid
// for the original sender; a recipient declining uses dismissLookoutShare.
export async function removeLookoutShare(id) {
  const response = await fetch(`${API_URL}/api/lookout-shares/${id}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() }
  });
  if (!response.ok) throw new Error('Failed to remove shared item');
  return response.json();
}
